import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, BellIcon, ChevronDownIcon, BuildingIcon, MenuIcon, ShieldAlertIcon, PlusIcon } from 'lucide-react';
import { Avatar } from '../ui/Avatar';
import { patients } from '../../data/patients';
import { cn } from '../../lib/cn';
interface TopBarProps {
  onOpenMenu: () => void;
}
export function TopBar({
  onOpenMenu
}: TopBarProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const results = query.length > 0 ? patients.filter(p => p.name.toLowerCase().includes(query.toLowerCase()) || p.mrn.toLowerCase().includes(query.toLowerCase())) : [];
  return <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-slate-200 bg-white/90 px-3 backdrop-blur sm:gap-3 sm:px-4" data-id="element-371" data-mp="ck5sq66kyqgf">
      <button type="button" onClick={onOpenMenu} className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 md:hidden" aria-label="Open main menu" data-id="element-372" data-mp="hcyzt0piuavo">
        <MenuIcon className="h-5 w-5" data-id="element-373" data-mp="2s3n5wtuu3p6c" />
      </button>

      {/* Facility switcher */}
      <button className="hidden lg:flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-374" data-mp="35va0mp94opn0">
        <BuildingIcon className="h-4 w-4 text-slate-400" data-id="element-375" data-mp="31wavp4necsvk" />
        <span data-id="element-376" data-mp="3fserh2dx59a5">Main Campus</span>
        <ChevronDownIcon className="h-3.5 w-3.5 text-slate-400" data-id="element-377" data-mp="3frdcwnxp24no" />
      </button>

      {/* Global patient search (MPI) */}
      <div className="relative min-w-0 flex-1 max-w-xl" data-id="element-378" data-mp="1lfgf8d8ns2k6">
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-379" data-mp="1lfyn674oo57x" />
        <input value={query} onChange={e => setQuery(e.target.value)} onFocus={() => setFocused(true)} onBlur={() => setTimeout(() => setFocused(false), 150)} placeholder="Search patient by name or MRN…" className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm outline-none transition-colors focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-100" aria-label="Global patient search" data-id="element-380" data-mp="1giyerpkd2qdn" />
        {focused && results.length > 0 && <div className="absolute mt-1.5 w-full rounded-lg border border-slate-200 bg-white shadow-panel overflow-hidden" data-id="element-381" data-mp="8a4tg7nk4ab8">
            {results.map(p => <button key={p.id} onMouseDown={() => navigate(`/patients/${p.id}`)} className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50" data-id="element-382" data-mp="h3htrunyae7c">
                <Avatar name={p.name} src={p.photo} size="sm" data-id="element-383" data-mp="r2yyo9vdoo29" />
                <div className="min-w-0" data-id="element-384" data-mp="115ks1khxu4zv">
                  <p className="text-sm font-medium text-slate-900 truncate" data-id="element-385" data-mp="116ng7knvltc3">{p.name}</p>
                  <p className="text-xs text-slate-500" data-id="element-386" data-mp="w5pzlciil7cp">
                    {p.mrn} · {p.age}
                    {p.sex[0]} · {p.bloodGroup}
                  </p>
                </div>
                {p.allergies.length > 0 && <ShieldAlertIcon className="ml-auto h-4 w-4 text-red-500 shrink-0" data-id="element-387" data-mp="n7w94i7fl7xg" />}
              </button>)}
          </div>}
      </div>

      <button onClick={() => navigate('/patients/new')} className="hidden sm:inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-xs font-semibold text-white hover:bg-brand-700 transition-colors" data-id="element-388" data-mp="1eyk57fu8zn49">
        <PlusIcon className="h-4 w-4" data-id="element-389" data-mp="1634eh7osho2w" />
        Register
      </button>

      {/* Break-glass */}
      <button title="Break-glass emergency access" className="hidden sm:inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-2.5 py-2 text-xs font-semibold text-red-600 hover:bg-red-100 transition-colors" data-id="element-390" data-mp="22oobjuytm0yy">
        <ShieldAlertIcon className="h-4 w-4" data-id="element-391" data-mp="1ndzpsyr14tbp" />
        Break-glass
      </button>

      <button className="relative rounded-lg p-2 text-slate-500 hover:bg-slate-100 transition-colors" aria-label="Notifications" data-id="element-392" data-mp="19e745hud13al">
        <BellIcon className="h-5 w-5" data-id="element-393" data-mp="1dfirthtobeg5" />
        <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white" data-id="element-394" data-mp="214ldd0rfhrfx" />
      </button>

      <div className="hidden items-center gap-2.5 border-l border-slate-200 pl-3 sm:flex" data-id="element-395" data-mp="263xvj87cuac6">
        <Avatar name="Dr. Imran Sethi" size="md" data-id="element-396" data-mp="1sct3ar6bgkbv" />
        <div className="hidden leading-tight lg:block" data-id="element-397" data-mp="1sc183hgmsom4">
          <p className="text-xs font-semibold text-slate-900" data-id="element-398" data-mp="1wd39o4y0tcr9">Dr. Imran Sethi</p>
          <p className="text-[11px] text-slate-500" data-id="element-399" data-mp="2k3pezogn4ixk">Consultant · Cardiology</p>
        </div>
        <ChevronDownIcon className="hidden h-4 w-4 text-slate-400 lg:block" data-id="element-400" data-mp="2ou5x3lveosq9" />
      </div>
    </header>;
}

// exported for possible reuse
export const _cn = cn;